import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import "./ManagePropertySubscription.scss";
import { Box, Modal } from "@mui/material";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import PropertyPlanUpgradeModal from "../properties/view/overview/PropertyPlanUpgradeModal";
import { SubscriptionApi } from "../../../utils/api/SubscriptionApi";
import { clientRoutes } from "../../../utils/routes";
import BackArrowIcon from "../../../components/icons/BackArrowIcon";
import TrueRoundIcon from "../../../components/icons/TrueRoundIcon";
import DownArrowIcon from "../../../components/icons/DownArrowIcon";
import ProtectedComponent from "../../../components/ProtectedComponent";
import NavigationLayout from "../navigation";

const ManagePropertySubscription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const historyId = searchParams.get("subscrpition_history_id");

  const [data, setData] = useState<any>(
    location.state?.subscriptionData || null,
  );
  const [loading, setLoading] = useState<boolean>(
    !location.state?.subscriptionData,
  );
  const [openFeatureIndex, setOpenFeatureIndex] = useState<number | null>(null);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUnavailableModalOpen, setIsUnavailableModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (historyId) {
        try {
          if (!data) setLoading(true);

          const res: any = await SubscriptionApi.getMySubscriptionHistory({
            id: historyId,
          });

          if (res?.data?.success && res?.data?.data) {
            setData(
              Array.isArray(res.data.data) ? res.data.data[0] : res.data.data,
            );
          }
        } catch (error) {
          console.error("Failed to fetch subscription", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [historyId]);

  const handleBack = () => {
    navigate(clientRoutes.manageSubscriptions);
  };

  const toggleFeature = (index: number) => {
    setOpenFeatureIndex(openFeatureIndex === index ? null : index);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "N/A";
    const date = dayjs(dateString);
    if (date.isValid()) {
      return date.format("MMMM D, YYYY");
    }
    const customDate = dayjs(dateString, "DD-MM-YYYY");
    if (customDate.isValid()) {
      return customDate.format("MMMM D, YYYY");
    }
    return "N/A";
  };

  const getListingTypeKey = () => {
    if (!data) return "sale";

    if (data.listing_type) {
      const typeStr =
        typeof data.listing_type === "string"
          ? data.listing_type
          : data.listing_type?.listing_type || "";

      if (
        typeStr.toLowerCase().includes("rent") ||
        typeStr.toLowerCase().includes("lease")
      )
        return "rent";
    }

    if (data.for_rent) return "rent";
    return "sale";
  };

  const getPriceDisplay = () => {
    if (data?.subscription_plan?.stripe_product_prices?.[0]) {
      const priceObj =
        data.subscription_plan.stripe_product_prices.find(
          (p: any) => p.currency === "aud",
        ) || data.subscription_plan.stripe_product_prices[0];
      return `AUD ${priceObj.unit_amount / 100}`;
    }
    return "AUD 0";
  };

  const handleCancelClick = () => {
    if (data?.is_subscription_used) {
      setIsUnavailableModalOpen(true);
    } else {
      setIsCancelModalOpen(true);
    }
  };

  const handleConfirmCancellation = async () => {
    try {
      setIsCancelModalOpen(false);
      setIsProcessing(true);

      const paymentIntentId = data?.stripe_payment_intent_id;

      if (!paymentIntentId) {
        setIsProcessing(false);
        toast.error("Payment ID not found. Cannot process refund.");
        return;
      }

      const res = await SubscriptionApi.cancelStripeSubscriptionWithRefund({
        stripe_payment_intent_id: paymentIntentId,
      });

      setIsProcessing(false);

      if (res?.data?.success) {
        toast.success(
          "Subscription cancelled and refund initiated successfully.",
        );
        handleBack();
      } else {
        toast.error(res?.data?.message || "Failed to cancel subscription.");
      }
    } catch (error) {
      setIsProcessing(false);
      console.error(error);
      toast.error("An error occurred. Please try again later.");
    }
  };

  const planFeatures = data?.subscription_plan?.plan_features || [];

  if (loading) return <div className="p-4">Loading details...</div>;
  if (!data) return <div className="p-4">Subscription not found.</div>;

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="manage-subscription-details-container">
          <div className="propertie-view-back-btn" onClick={handleBack}>
            <div className="back-icon">
              <BackArrowIcon />
            </div>
            <span className="bakc-text-contain">Back</span>
          </div>

          <div className="page-header">
            <p className="main-header">Manage subscription</p>
          </div>

          <div className="subscription-meta-data">
            <div className="meta-row">
              <div className="meta-item">
                <label>Subscription id</label>
                <p>{data?.subscription_plan?._id || "N/A"}</p>
              </div>
              <div className="meta-item">
                <label>Stripe subscription id</label>
                <p>{data?.stripe_subscription_id || "-"}</p>
              </div>
              <div className="meta-item">
                <label>Status</label>
                <div className="status-badge-green">Active</div>
              </div>
            </div>
            <div className="meta-row second-row">
              <div className="meta-item">
                <label>Start date</label>
                <p>
                  {formatDate(
                    data?.subscription_start_date || data?.created_at,
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="section-subtitle">Property marketing plan</div>

          <div className="details-card">
            <div className="card-top-row">
              <div className="card-info">
                <h3 className="plan-name">
                  {data?.subscription_plan?.plan_name || "Standard Plan"}
                </h3>
                <p className="plan-type-text">
                  Property Marketing Fee One-off (
                  {data?.for_sale ? "For Sale" : "For Rent"})
                </p>
                <p className="property-address">{data?.property_address}</p>
              </div>
              <div className="card-status">
                <span className="status-dot"></span>
                <span className="status-text">Active</span>
              </div>
            </div>

            <div className="card-price-row">
              <p className="price-text">{getPriceDisplay()}</p>
            </div>
          </div>

          <div className="features-section">
            <p className="features-main-title">Plan features</p>

            <div className="features-grid">
              {planFeatures.length > 0 ? (
                planFeatures.map((feature: any, index: number) => (
                  <div
                    className="feature-item-wrapper"
                    key={feature._id || index}
                  >
                    <div
                      className="feature-header"
                      onClick={() => toggleFeature(index)}
                    >
                      <div className="feature-title-group">
                        <span className="check-icon">
                          <TrueRoundIcon />
                        </span>
                        <span className="feature-title">{feature.title}</span>
                      </div>
                      <div
                        className={`arrow-icon ${openFeatureIndex === index ? "rotate" : ""}`}
                      >
                        <DownArrowIcon />
                      </div>
                    </div>

                    <div
                      className={`feature-description ${openFeatureIndex === index ? "expanded" : ""}`}
                    >
                      <p>{feature.description}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p
                  style={{
                    color: "#666",
                    fontFamily: "Inter",
                    fontSize: "14px",
                  }}
                >
                  No features listed for this plan.
                </p>
              )}
            </div>
          </div>

          <div className="action-footer">
            <button
              className="change-plan-btn"
              onClick={() => setShowUpgradeModal(true)}
            >
              Change Plan
            </button>
            <button className="cancel-plan-btn" onClick={handleCancelClick}>
              Cancel subscription
            </button>
          </div>

          {showUpgradeModal && (
            <PropertyPlanUpgradeModal
              open={showUpgradeModal}
              onClose={() => setShowUpgradeModal(false)}
              propertyId={data?.property_id || data?.property?._id}
              propertyAddress={data?.property_address}
              listingTypeKey={getListingTypeKey()}
              currentPlan={data?.subscription_plan}
            />
          )}

          <Modal
            open={isCancelModalOpen}
            onClose={() => setIsCancelModalOpen(false)}
            className="property-cancel-modal-overlay"
          >
            <Box className="property-cancel-modal-content">
              <p className="cancel-modal-title">Cancel subscription?</p>

              <div className="cancel-modal-body">
                <p className="warning-text">
                  You are about to deactivate marketing features for
                  <br />
                  {data?.property_address}
                </p>
                <p className="warning-secondary-text">
                  Once confirmed, this property will be marked as inactive and
                  all associated tools will be disabled immediately.
                </p>
              </div>

              <div className="refund-info-section">
                <p className="refund-title">Refund information</p>
                <p className="refund-desc">
                  Refunds typically take 5–10 business days to reflect on your
                  bank statement. Please note that while we do not charge any
                  additional fees to process a refund, the original transaction
                  fees from the initial payment are non-refundable.
                </p>
              </div>

              <div className="cancel-modal-actions">
                <button
                  className="btn-modal-cancel"
                  onClick={() => setIsCancelModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-modal-confirm"
                  onClick={handleConfirmCancellation}
                >
                  Confirm cancellation
                </button>
              </div>
            </Box>
          </Modal>

          <Modal
            open={isProcessing}
            className="property-cancel-modal-overlay"
            onClose={() => {}}
          >
            <Box className="property-cancel-modal-content processing-content">
              <div className="processing-spinner"></div>
              <p className="processing-text">
                Processing your request <br /> Please wait...
              </p>
            </Box>
          </Modal>

          <Modal
            open={isUnavailableModalOpen}
            onClose={() => setIsUnavailableModalOpen(false)}
            className="property-cancel-modal-overlay"
          >
            <Box className="unavailable-content-modal">
              <div className="unavailable-icon-container">
                <img
                  src="/images/UnavailableIcon.svg"
                  alt="Unavailable"
                  className="unavailable-icon"
                />
              </div>

              <p className="unavailable-title">Cancellation Unavailable!</p>

              <div className="unavailable-body">
                <p className="unavailable-text">
                  You have already accessed the marketing features for
                  <br />
                  {data?.property_address}.
                </p>
                <p className="unavailable-text">
                  Per our policy, subscriptions cannot be canceled or refunded
                  once the services have been utilized. <br />
                  For more details, please review our{" "}
                  <a href="#" className="policy-link">
                    Cancellation and refund policy.
                  </a>
                </p>
              </div>

              <button
                className="btn-modal-done"
                onClick={() => setIsUnavailableModalOpen(false)}
              >
                Done
              </button>
            </Box>
          </Modal>
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default ManagePropertySubscription;
